import type { Polar } from "@polar-sh/sdk";
import {
	APIError,
	createAuthEndpoint,
	getSessionFromCtx,
} from "better-auth/api";
import * as z from "zod/v4";
import type { Product } from "../types";

export interface CheckoutOptions {
	/**
	 * Optional list of slug -> productId mappings for easy slug checkouts
	 */
	products?: Product[] | (() => Promise<Product[]>);
	/**
	 * Checkout Success URL
	 */
	successUrl?: string;
	/**
	 * Checkout Return URL
	 */
	returnUrl?: string;
	/**
	 * Only allow authenticated customers to checkout
	 */
	authenticatedUsersOnly?: boolean;
	/**
	 * Checkout theme
	 */
	theme?: "light" | "dark";
}

export const CheckoutParams = z.object({
	products: z.union([z.array(z.string()), z.string()]).optional(),
	slug: z.string().optional(),
	referenceId: z.string().optional(),
	customFieldData: z
		.record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
		.optional(),
	metadata: z
		.record(z.string(), z.union([z.string().max(500), z.number(), z.boolean()]))
		.refine((obj) => Object.keys(obj).length <= 50, {
			message: "Metadata can have at most 50 key-value pairs",
		})
		.refine((obj) => Object.keys(obj).every((key) => key.length <= 40), {
			message: "Metadata keys must be at most 40 characters",
		})
		.optional(),
	allowDiscountCodes: z.coerce.boolean().optional(),
	discountId: z.string().optional(),
	redirect: z.coerce.boolean().optional(),
	embedOrigin: z.url().optional(),
	successUrl: z
		.string()
		.refine((val) => val.startsWith("/") || URL.canParse(val), {
			message: "Must be a valid URL or a relative path starting with /",
		})
		.optional(),
	returnUrl: z
		.string()
		.refine((val) => val.startsWith("/") || URL.canParse(val), {
			message: "Must be a valid URL or a relative path starting with /",
		})
		.optional(),
	allowTrial: z.boolean().optional(),
	trialInterval: z.enum(["day", "week", "month", "year"]).optional(),
	trialIntervalCount: z.number().int().min(1).max(1000).optional(),
});

export type CheckoutParams = z.infer<typeof CheckoutParams>;

export const checkout =
	(checkoutOptions: CheckoutOptions = {}) =>
	(polar: Polar) => {
		return {
			checkout: createAuthEndpoint(
				"/checkout",
				{
					method: "POST",
					body: CheckoutParams,
				},
				async (ctx) => {
					const session = await getSessionFromCtx(ctx);

					let productIds: string[] = [];

					if (ctx.body.slug) {
						const resolvedProducts = await (typeof checkoutOptions.products ===
						"function"
							? checkoutOptions.products()
							: checkoutOptions.products);

						const productId = resolvedProducts?.find(
							(product) => product.slug === ctx.body.slug,
						)?.productId;

						if (!productId) {
							throw new APIError("BAD_REQUEST", {
								message: "Product not found",
							});
						}

						productIds = [productId];
					} else {
						productIds = Array.isArray(ctx.body.products)
							? ctx.body.products.filter((id) => id !== undefined)
							: [ctx.body.products].filter((id) => id !== undefined);
					}

					if (checkoutOptions.authenticatedUsersOnly) {
						if (!session?.user.id) {
							throw new APIError("UNAUTHORIZED", {
								message: "You must be logged in to checkout",
							});
						}

						if (session.user["isAnonymous"]) {
							throw new APIError("UNAUTHORIZED", {
								message: "Anonymous users are not allowed to checkout",
							});
						}
					}

					const successUrl = ctx.body.successUrl ?? checkoutOptions.successUrl;
					const returnUrl = ctx.body.returnUrl ?? checkoutOptions.returnUrl;

					try {
						const checkout = await polar.checkouts.create({
							externalCustomerId: session?.user.id,
							products: productIds,
							successUrl: successUrl
								? new URL(
										successUrl,
										ctx.request?.url ?? ctx.context.baseURL,
									).toString()
								: undefined,
							metadata: ctx.body.referenceId
								? {
										referenceId: ctx.body.referenceId,
										...ctx.body.metadata,
									}
								: ctx.body.metadata,
							customFieldData: ctx.body.customFieldData,
							allowDiscountCodes: ctx.body.allowDiscountCodes ?? true,
							discountId: ctx.body.discountId,
							embedOrigin: ctx.body.embedOrigin,
							allowTrial: ctx.body.allowTrial,
							trialInterval: ctx.body.trialInterval,
							trialIntervalCount: ctx.body.trialIntervalCount,
							returnUrl: returnUrl
								? new URL(
										returnUrl,
										ctx.request?.url ?? ctx.context.baseURL,
									).toString()
								: undefined,
						});

						const redirectUrl = new URL(checkout.url);

						if (checkoutOptions.theme) {
							redirectUrl.searchParams.set("theme", checkoutOptions.theme);
						}

						return ctx.json({
							url: redirectUrl.toString(),
							redirect: ctx.body.redirect ?? true,
						});
					} catch (e: unknown) {
						if (e instanceof Error) {
							ctx.context.logger.error(
								`Polar checkout creation failed. Error: ${e.message}`,
							);
						}

						throw new APIError("INTERNAL_SERVER_ERROR", {
							message: "Checkout creation failed",
						});
					}
				},
			),
		};
	};
